import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { FormatHelper } from '../../../helpers/FormatHelper';
import { strings } from '../../../resources/strings';
import { CustomerGeneralOption } from '../../../enums/CustomerGeneralOption';
import { Customer } from '../../../models/Customer';
import { CustomerAddress } from '../../../models/CustomerAddress';
import ServicesContext from '../../../contexts/ServicesContext';
import { DropdownButton, DropdownButtonColor, DropdownButtonDirection } from '../../../common-components/DropdownButton/DropdownButton';
import { BadgeTitled } from '../../../common-components/BadgeTitled/BadgeTitled';
import { If } from '../../../common-components/If/If';
import { 
    Container, CustomerName, DetailsToogleButton,
    Values, Options, PrimaryContent, SecondaryContent,
    SecondaryContentCard, SecondaryContentCardHeader,
    SecondaryContentCardItem
} from './styles';
import { CustomerOriginHelper } from '../../../helpers/CustomerOriginHelper';

interface ICustomerListItemProps {
    customer: Customer;
    handleGeneralOption: (customer: Customer, key: any) => void;
}

const CustomerListItem: React.FC<ICustomerListItemProps> = ({
    customer,
    handleGeneralOption
}) => {

    const [ expanded, setExpanded ] = React.useState<boolean>(false);
    const [ addresses, setAddresses ] = React.useState<CustomerAddress[]>([]);
    const [ lastUpdatedAt, setLastUpdatedAt ] = React.useState<Date|null|undefined>(new Date(0,0,0));

    const {
        getCustomerByIdService
    } = React.useContext(ServicesContext);

    React.useEffect(() => {
        const customerDate = !!customer?.updatedAt ? new Date(customer.updatedAt) : new Date(0,0,0);
        const customerHasBeenUpdated = customerDate && lastUpdatedAt ? customerDate > lastUpdatedAt : false;
        if (customerHasBeenUpdated) {
            setExpanded(false);
        }
    }, [ customer, lastUpdatedAt ]);

    const listAddresses = React.useMemo(() => {
        const hasAnyAddress = addresses?.length > 0;

        return (
            <SecondaryContentCard>
                <SecondaryContentCardHeader>Endereços</SecondaryContentCardHeader>
                <If condition={!hasAnyAddress}>
                    <SecondaryContentCardItem>
                        Nenhum endereço
                    </SecondaryContentCardItem>
                </If>
                <If condition={hasAnyAddress}>
                    {addresses.map(address => {

                        let textAddress = '';
                        if (address.address) textAddress += (textAddress ? ', ' : '') + address.address;
                        if (address.additionalAddress) textAddress += (textAddress ? ', ' : '') + address.additionalAddress;
                        if (address.addressNumber) textAddress += (textAddress ? ', ' : '') + address.addressNumber;
                        if (address.neighborhood) textAddress += (textAddress ? ', ' : '') + address.neighborhood;
                        if (address.zip) textAddress += (textAddress ? ', ' : '') + `CEP ${address.zip}`;
                        if (address.city) textAddress += (textAddress ? ', ' : '') + address.city;
                        if (address.provinceCode) textAddress += (textAddress ? ', ' : '') + address.provinceCode;

                        return (
                            <SecondaryContentCardItem key={`address-${address.id}`}>
                                {textAddress}
                            </SecondaryContentCardItem>
                        )
                    })}
                </If>
            </SecondaryContentCard>
        );
    }, [addresses, lastUpdatedAt]);

    return (
        <Container>
            <PrimaryContent>
                <DetailsToogleButton onClick={() => getCustomer()}>
                    <FontAwesomeIcon icon={expanded ? faCaretDown : faCaretRight} color='gray' size='1x' />
                </DetailsToogleButton>
                <Values onClick={() => getCustomer()}>
                    <CustomerName>{customer.firstName} {customer.lastName}</CustomerName>
                    <If condition={!!customer.email}>
                        <BadgeTitled text={customer.email} />
                    </If>
                    <If condition={!!customer.phone}>
                        <BadgeTitled title='Tel' text={customer.phone} />
                    </If>
                    <If condition={!!customer.origin}>
                        <BadgeTitled title='Origem' text={CustomerOriginHelper.typeToText(customer.origin)} />
                    </If>
                    <If condition={!!customer.document}>
                        <BadgeTitled title='Doc' text={FormatHelper.formatPersonalIdentificationNumber(customer.document)} />
                    </If>
                    <If condition={!!customer.createdAt}>
                        <BadgeTitled title='Cad' text={FormatHelper.formatDate(customer.createdAt)} />
                    </If>
                    <If condition={!!customer.updatedAt}>
                        <BadgeTitled title='Alt' text={FormatHelper.formatDate(customer.updatedAt)} />
                    </If>
                </Values>
                <Options>
                    <DropdownButton
                        items={[
                            {
                                key: CustomerGeneralOption.CustomerNotes,
                                text: strings.Notes
                            },
                            {
                                key: CustomerGeneralOption.Edit,
                                text: strings.Edit
                            },
                        ]}
                        defaultStyle={true}
                        small={true}
                        direction={DropdownButtonDirection.DropToDown}
                        buttonColor={DropdownButtonColor.Secondary}
                        outlineButton
                        text={''}
                        clickItem={(key) => handleGeneralOption(customer, key)}
                    />
                </Options>
            </PrimaryContent>
            <If condition={expanded}>
                <SecondaryContent>
                    {listAddresses}
                </SecondaryContent>
            </If>
        </Container>
    );

    function getCustomer() {
        if (!customer) return;

        if (expanded) {
            setExpanded(false);
            return;
        }
        
        getCustomerByIdService.handle({id: customer.id})
            .then(result => {
                setAddresses(result?.value?.customer.customerAddresses || []);
                const customerDate = !!result?.value?.customer?.updatedAt ? new Date(result.value.customer.updatedAt) : new Date(0,0,0);
                setLastUpdatedAt(customerDate || new Date(0,0,0));
                setExpanded(true);
            });
    }
}

export {CustomerListItem};